import { useDebouncedCallback } from '@mantine/hooks'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useMyInfo } from 'src/services/apollo/auth'
import {
  executeKaisuukenCheck,
  useKaisuukenCheckMatching,
} from 'src/services/orpc/kaisuuken'

// ---------------------------------------------------------------------------
// Shared types
// ---------------------------------------------------------------------------

export type KaisuukenMatchResult = {
  totalMatching: number
  selectedKaisuukenId: string | undefined
  isChecking: boolean
  refresh: () => void
}

type MatchState = {
  totalMatching: number
  selectedKaisuukenId: string | undefined
}

const INITIAL_STATE: MatchState = {
  totalMatching: 0,
  selectedKaisuukenId: undefined,
}

type CheckResult = {
  total?: number
  matches?: Array<{ kaisuukens: Array<{ id: string }> }>
}

// ---------------------------------------------------------------------------
// Core state machine — shared by both hooks
// ---------------------------------------------------------------------------

/**
 * Handles request deduplication, stale-response guarding, and result extraction.
 */
function useKaisuukenMatchBase() {
  const [matchState, setMatchState] = useState<MatchState>(INITIAL_STATE)
  const [isChecking, setIsChecking] = useState(false)
  const requestIdRef = useRef(0)

  /** Run an async check with stale-request guarding. */
  const executeCheck = useCallback(
    async (checkFn: () => Promise<CheckResult | null | undefined>) => {
      const currentRequestId = ++requestIdRef.current

      try {
        const result = await checkFn()
        if (currentRequestId !== requestIdRef.current) return

        const total = result?.total ?? 0
        const selectedId =
          total > 0
            ? (result?.matches?.flatMap(m => m.kaisuukens) ?? [])[0]?.id
            : undefined
        setMatchState({ totalMatching: total, selectedKaisuukenId: selectedId })
      } catch {
        if (currentRequestId !== requestIdRef.current) return
        setMatchState(INITIAL_STATE)
      } finally {
        if (currentRequestId === requestIdRef.current) {
          setIsChecking(false)
        }
      }
    },
    [],
  )

  /** Invalidate in-flight requests and reset to initial state. */
  const reset = useCallback(() => {
    ++requestIdRef.current
    setMatchState(INITIAL_STATE)
    setIsChecking(false)
  }, [])

  const startChecking = useCallback(() => {
    setIsChecking(true)
  }, [])

  /** Clear the selected card ID (e.g. before a refresh to prevent reuse). */
  const clearSelectedId = useCallback(() => {
    setMatchState(prev => ({ ...prev, selectedKaisuukenId: undefined }))
  }, [])

  return {
    totalMatching: matchState.totalMatching,
    selectedKaisuukenId: matchState.selectedKaisuukenId,
    isChecking,
    executeCheck,
    reset,
    startChecking,
    clearSelectedId,
  }
}

// ---------------------------------------------------------------------------
// Generation task matching (debounced, multi-parameter)
// ---------------------------------------------------------------------------

/**
 * Checks whether current generation parameters match any available kaisuuken
 * (benefit card / free generation) cards.
 *
 * UI state machine:
 * - When match-relevant params change, `isChecking` flips to true **immediately**
 *   (before the debounce fires) so the badge can show a spinner right away.
 * - `totalMatching` keeps its previous value until the API responds, so the
 *   "Free Generation" label stays visible during the transition instead of
 *   flickering away and back.
 * - Only after the API resolves does `totalMatching` update and `isChecking`
 *   flip back to false.
 */
export function useKaisuukenMatch(
  parameters: Record<string, unknown> | undefined,
): KaisuukenMatchResult {
  const { mutateAsync: checkMatching } = useKaisuukenCheckMatching()
  const { data: me } = useMyInfo()
  const {
    totalMatching,
    selectedKaisuukenId,
    isChecking,
    executeCheck,
    reset,
    startChecking,
    clearSelectedId,
  } = useKaisuukenMatchBase()

  // Keep a ref to latest parameters so the debounced callback always uses fresh values
  const parametersRef = useRef(parameters)
  parametersRef.current = parameters

  const doCheck = useCallback(
    (params: Record<string, unknown>) =>
      executeCheck(() =>
        checkMatching({
          type: 'generation-task',
          parameters: params,
        }),
      ),
    [executeCheck, checkMatching],
  )

  const debouncedCheck = useDebouncedCallback(doCheck, 500)

  // Only recheck when parameters that affect kaisuuken matching change.
  // Include auth state so we recheck when user logs in, and skip when logged out.
  const matchKey = parameters && me ? matchKeyFromParams(parameters) : ''
  const prevMatchKeyRef = useRef<string | null>(null)

  useEffect(() => {
    // No parameters — reset everything
    if (!parameters || !matchKey) {
      prevMatchKeyRef.current = matchKey
      reset()
      debouncedCheck.cancel()
      return
    }

    const matchKeyChanged = matchKey !== prevMatchKeyRef.current
    prevMatchKeyRef.current = matchKey

    // Immediately mark as checking so the UI can show a spinner in the badge
    // while keeping the previous totalMatching / "Free Generation" label.
    if (matchKeyChanged) {
      startChecking()
    }

    debouncedCheck(parametersRef.current!)
  }, [matchKey, doCheck, debouncedCheck, reset, startChecking])

  const refresh = useCallback(() => {
    if (!parametersRef.current) return
    debouncedCheck.cancel()
    // Immediate re-check (no debounce) — used after task submission.
    // Clear selectedKaisuukenId right away so a rapid resubmit won't
    // reuse an already-consumed card before the API responds.
    clearSelectedId()
    startChecking()
    doCheck(parametersRef.current)
  }, [doCheck, debouncedCheck, clearSelectedId, startChecking])

  return {
    totalMatching,
    selectedKaisuukenId,
    isChecking,
    refresh,
  }
}

// ---------------------------------------------------------------------------
// Training task matching (immediate, baseModelId only)
// ---------------------------------------------------------------------------

/**
 * Checks whether the selected base model matches any available kaisuuken
 * (benefit card) for training tasks.
 *
 * Simpler than useKaisuukenMatch because training only depends on baseModelId
 * — no debouncing needed since users don't rapidly change the base model.
 */
export function useKaisuukenMatchTraining(
  baseModelId: string | undefined,
): KaisuukenMatchResult {
  const { data: me } = useMyInfo()
  const {
    totalMatching,
    selectedKaisuukenId,
    isChecking,
    executeCheck,
    reset,
    startChecking,
    clearSelectedId,
  } = useKaisuukenMatchBase()

  const baseModelIdRef = useRef(baseModelId)
  baseModelIdRef.current = baseModelId

  const doCheck = useCallback(
    (modelId: string) =>
      executeCheck(() =>
        executeKaisuukenCheck({
          type: 'training-task',
          baseModelId: modelId,
        }),
      ),
    [executeCheck],
  )

  const isLoggedIn = Boolean(me)

  useEffect(() => {
    if (!baseModelId || !isLoggedIn) {
      reset()
      return
    }

    startChecking()
    doCheck(baseModelId)
  }, [baseModelId, isLoggedIn, doCheck, reset, startChecking])

  const refresh = useCallback(() => {
    if (!baseModelIdRef.current) return
    clearSelectedId()
    startChecking()
    doCheck(baseModelIdRef.current)
  }, [doCheck, clearSelectedId, startChecking])

  return {
    totalMatching,
    selectedKaisuukenId,
    isChecking,
    refresh,
  }
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Subset of task parameters that influence kaisuuken card matching. */
type MatchRelevantParams = {
  modelId?: string
  sceneId?: string
  i2vPro?: { model?: string }
  t2i2v?: { model?: string }
  chat?: { modelId?: string }
}

/**
 * Derives a stable key from only the parameters that affect card matching,
 * so the check doesn't re-fire on irrelevant changes like prompt edits.
 *
 * Server-side matching cares about model identity and task type, not
 * content fields like prompts, seeds, or durations. For i2vPro / t2i2v tasks
 * the server resolves the effective modelId from `i2vPro.model` (see
 * `getGeneratorI2vProModelIdFromParameters` on the backend), so switching
 * between i2v models (e.g. v4.0 → v3.0 Lite) must trigger a recheck.
 */
function matchKeyFromParams(params: Record<string, unknown>): string {
  // Cast to the known subset — the full params object has many more fields
  // but only these affect card matching on the server side.
  const obj = params as MatchRelevantParams
  const parts: unknown[] = [
    obj.modelId,
    obj.sceneId,
    obj.i2vPro?.model ?? (obj.i2vPro ? '' : undefined),
    obj.t2i2v?.model ?? (obj.t2i2v ? '' : undefined),
    Boolean(obj.chat),
    obj.chat?.modelId,
  ]
  return JSON.stringify(parts)
}
